import React, {useCallback, useEffect} from 'react'
import type {Category} from 'src/api/saleor/generated'
import {CategoryDocument} from 'src/api/saleor/generated'
import styled, {css} from 'styled-components'
import {ifProp, palette} from 'styled-tools'
import ArrowRightLineIcon from 'remixicon-react/ArrowRightLineIcon'
import {useApolloClient} from '@apollo/client'
import {useWindowSize} from 'react-use'
import {gridTheme} from 'src/themes'
import type {Maybe} from 'src/utils/Maybe'
import {isNotNull} from 'src/utils/isNotNull'
import {paths} from 'src/utils/paths'
import Link from '../common/Link'
import {H6, Title6} from '../typography'

const Wrapper = styled.div`
  z-index: 400;
`
const Box = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  padding: 0 16px;
  margin-bottom: 4px;
  border: 1px solid ${palette('black')};
  background-color: ${palette('white')};

  ${Title6} {
    display: flex;
    align-items: center;
  }

  svg {
    margin-right: 8px;
    color: ${palette('red')};
  }
`

const SubCategoryList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid ${palette('black')};
  background-color: ${palette('white')};
`

interface SubCategoryProps {
  selected?: boolean
}

const underlineStyles = css`
  &:after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 0;
    right: 0;
    border-bottom: 2px solid ${palette('red')};
    animation: slidein 0.25s;
  }
`

const SubCategory = styled.div<SubCategoryProps>`
  position: relative;
  padding: 5px 0;
  cursor: pointer;
  margin-bottom: 5px;
  ${ifProp({selected: true}, underlineStyles, '')};

  &:hover {
    @keyframes slidein {
      from {
        width: 0;
      }

      to {
        width: 100%;
      }
    }

    ${underlineStyles};
  }
`

interface Props {
  category?: Category
  isLandingPage?: boolean
  setShowMobileSubCategoryList?: (value: React.SetStateAction<boolean>) => void
}

export default function CategorySidebar({
  category,
  isLandingPage,
  setShowMobileSubCategoryList,
}: Props): JSX.Element {
  const {width} = useWindowSize()
  const client = useApolloClient()
  useEffect(() => {
    // if level1
    const edges = category?.children?.edges ?? []
    // if level2
    const parentEdges = category?.parent?.children?.edges
    // Prefetch
    // either level1 or level2
    const categories = edges ?? parentEdges ?? []
    // all req categories slugs
    const slugs = categories.map((e) => e.node.slug)
    slugs.map(async (slug) =>
      client.query({query: CategoryDocument, variables: {slug}}),
    )
  }, [category, client])

  const handleShowMobileSubCategoryList = useCallback(() => {
    if (!setShowMobileSubCategoryList) {
      return
    }
    setShowMobileSubCategoryList(false)
  }, [setShowMobileSubCategoryList])
  const parent = category?.parent
  const edges = category?.children?.edges
  const showCategory = isNotNull(category)
  const showEdges = showCategory && isNotNull(edges) && edges.length
  const showParent = showCategory && !showEdges

  return (
    <Wrapper>
      {width > gridTheme.breakpoints.md && (
        <Box>
          <Link href={paths.categories} shallow>
            <H6>Vöruflokkar</H6>
          </Link>
        </Box>
      )}
      {parent ? (
        <Box>
          <Link
            href={`${paths.categories}/${parent.slug}`}
            shallow
            onClick={handleShowMobileSubCategoryList}
          >
            <Title6>
              <ArrowRightLineIcon /> {parent.name}
            </Title6>
          </Link>
        </Box>
      ) : null}
      {showEdges ? (
        <>
          <Box>
            <Title6>
              <ArrowRightLineIcon /> {category.name}
            </Title6>
          </Box>

          <SubCategoryList>
            <Link
              href={`${paths.categories}/${category?.slug || ''}`}
              // href={`${paths.account}`}
              shallow
              onClick={handleShowMobileSubCategoryList}
            >
              <SubCategory selected={!isLandingPage}>Allar vörur</SubCategory>
            </Link>
            {edges.map((subCategory) => (
              <Link
                shallow
                key={subCategory.node.id}
                href={`${paths.categories}/${subCategory.node?.slug || ''}`}
                onClick={handleShowMobileSubCategoryList}
              >
                <SubCategory>{subCategory.node.name}</SubCategory>
              </Link>
            ))}
          </SubCategoryList>
        </>
      ) : null}
      {showParent ? (
        <SubCategoryList>
          <Link
            href={`${paths.categories}/${category.parent?.slug || ''}`}
            shallow
            onClick={handleShowMobileSubCategoryList}
          >
            <SubCategory selected={category.parent?.id === category.id}>
              Allar vörur
            </SubCategory>
          </Link>
          {category?.parent?.children?.edges.map((subCategory) => (
            <Link
              key={subCategory.node.id}
              href={`${paths.categories}/${subCategory.node?.slug || ''}`}
              shallow
              onClick={handleShowMobileSubCategoryList}
            >
              <SubCategory selected={subCategory.node.id === category.id}>
                {subCategory.node.name}
              </SubCategory>
            </Link>
          ))}
        </SubCategoryList>
      ) : null}
    </Wrapper>
  )
}
interface CategoryListProps {
  category: Maybe<Category>
}

CategorySidebar.CategoryList = ({
  category,
}: CategoryListProps): JSX.Element | null => {
  const children = category?.children?.edges ?? []
  const parent = category?.parent
  const parentChildren = parent?.children?.edges ?? []
  const showChildren = !!children.length
  const showParent = !showChildren && !!parent
  if (!category || (!showParent && !showChildren)) {
    return null
  }
  if (showChildren) {
    return (
      <>
        <Box>
          <Title6>
            <ArrowRightLineIcon /> {category.name}
          </Title6>
        </Box>

        <SubCategoryList>
          <Link href={`${paths.categories}/${category?.slug || ''}`} shallow>
            <SubCategory selected>Allar vörur</SubCategory>
          </Link>
          {children.map((subCategory) => (
            <Link
              shallow
              key={subCategory.node.id}
              href={`${paths.categories}/${subCategory.node?.slug || ''}`}
            >
              <SubCategory>{subCategory.node.name}</SubCategory>
            </Link>
          ))}
        </SubCategoryList>
      </>
    )
  }
  if (!parent) {
    // This should not happen but, this is so remind TS that type is in fact not null
    return null
  }

  return (
    <SubCategoryList>
      <Link href={`${paths.categories}/${parent.slug ?? ''}`} shallow>
        <SubCategory selected={parent.id === category.id}>
          Allar vörur
        </SubCategory>
      </Link>
      {parentChildren.map((subCategory) => (
        <Link
          key={subCategory.node.id}
          href={`${paths.categories}/${subCategory.node?.slug || ''}`}
          shallow
        >
          <SubCategory selected={subCategory.node.id === category.id}>
            {subCategory.node.name}
          </SubCategory>
        </Link>
      ))}
    </SubCategoryList>
  )
}
